import { useState, useEffect } from "react";
import { StandardProps } from "@/types/types";
import styles from "./BackToTop.module.scss";
import animateScroll from "react-scroll/modules/mixins/animate-scroll";
import classNames from "classnames/bind";
import BackToTopIcon from "./BackToTopIcon";

const JL_COMPARE_FOOTER_HIDDEN_EVENT = "jl-compare-footer-hidden";
const BACK_TO_TOP_THRESHOLD = 1200; // 400 (avg product height) * 3 - derived from legacy implementation

export function BackToTop({ configProps }: Pick<StandardProps, "configProps">) {
  const cx = classNames.bind(styles);
  const initialScroll = (configProps?.initialScroll as number) || 0;
  const [scrollTop, setScrollTop] = useState(initialScroll);
  const [isCompareActive, setIsCompareActive] = useState(false);
  const isActive = scrollTop >= BACK_TO_TOP_THRESHOLD;

  const setScrollPosition = () => setScrollTop(window.scrollY);

  const handleCompareFooterHiddenEvent = (event: CustomEventInit) => {
    const { isHidden } = event.detail;
    setIsCompareActive(!isHidden);
  };

  useEffect(function subscribeToScrollPosition() {
    window.addEventListener("scroll", setScrollPosition);

    return function cleanupScrollPositionListener() {
      window.removeEventListener("scroll", setScrollPosition);
    };
  }, []);

  useEffect(function subscribeToCompareFooterHiddenEvent() {
    window.addEventListener(
      JL_COMPARE_FOOTER_HIDDEN_EVENT,
      handleCompareFooterHiddenEvent,
    );

    return function cleanupCompareFooterHiddenListener() {
      window.removeEventListener(
        JL_COMPARE_FOOTER_HIDDEN_EVENT,
        handleCompareFooterHiddenEvent,
      );
    };
  }, []);

  const handleButtonClick = () => {
    animateScroll.scrollToTop();
    // Focus is set to prevent screen readers "getting lost" when the button is activated
    const headingTerm = document.querySelector('[data-testid="heading-term"]');
    if (headingTerm) {
      (headingTerm as HTMLElement).focus();
    }
  };

  return (
    <div
      data-testid="component-back-to-top"
      className={cx("back-to-top", {
        "back-to-top--active": isActive,
        "back-to-top--compare-active": isCompareActive,
      })}
    >
      <div className={styles["back-to-top__container"]}>
        <button
          className={styles["back-to-top__button"]}
          type="button"
          data-testid="back-to-top-button"
          aria-label="Back to top"
          onClick={handleButtonClick}
        >
          <BackToTopIcon />
        </button>
      </div>
    </div>
  );
}
